// src/shared/hooks/useHttpClient.js
import { useCallback, useRef, useEffect } from "react";
import { useErrorHandler } from "./useErrorHandler";

export const useHttpClient = () => {
  const { handleError } = useErrorHandler();
  const activeHttpRequests = useRef([]);

  const sendRequest = useCallback(
    async (url, method = "GET", body = null, headers = {}, options = {}) => {
      const httpAbortCtrl = new AbortController();
      activeHttpRequests.current.push(httpAbortCtrl);

      try {
        const response = await fetch(url, {
          method,
          cache: "no-store",
          body,
          headers,
          signal: httpAbortCtrl.signal,
        });

        const responseData = await response.json();

        activeHttpRequests.current = activeHttpRequests.current.filter(
          (reqCtrl) => reqCtrl !== httpAbortCtrl,
        );

        if (!response.ok) {
          const error = new Error(responseData.message);
          error.response = {
            status: response.status,
            data: responseData,
          };
          throw error;
        }

        return responseData;
      } catch (err) {
        // Verificar se é erro de contingência E-fatura
        // Esses erros são tratados por dialogs específicos, não por toast
        const isErroContingencia =
          err.response?.status === 503 &&
          err.response?.data?.permiteContingencia === true;

        // Só mostra toast se não for erro de contingência e se não foi pedido skip
        if (!options.skipErrorHandler && !isErroContingencia) {
          handleError(err);
        }
        throw err;
      }
    },
    [handleError],
  );

  useEffect(() => {
    return () => {
      activeHttpRequests.current.forEach((abortCtrl) => abortCtrl.abort());
    };
  }, []);

  return { sendRequest };
};
